import { useEffect } from "react";
import { Link } from "react-router-dom";
import { data } from "../data";
import Reveal from "../components/Reveal";

export default function Work() {
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, []);

  // Hero / featured mural
  const featured = data[0];
  const rest = data.slice(1);

  return (
    <main className="bg-[var(--bg)] text-[var(--bone)] pt-32 pb-24">
      {/* HERO */}
      <section className="px-5 md:px-10">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.32em] text-[var(--muted)] mb-6">
              <span className="w-8 h-px bg-[var(--clay)]" />
              Mural Arts · Selected Works
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="font-display font-light text-[clamp(3rem,9vw,8rem)] leading-[0.92] tracking-tight">
              Walls, painted <span className="italic font-extralight">by</span>{" "}
              <span className="text-[var(--clay)]">hand.</span>
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-8 max-w-2xl text-[var(--muted)] text-[17px] leading-relaxed">
              From a quiet café in Delhi to a flyover in Allahabad, every mural
              you see here was sketched, scaled, and brushed by TABA — one wall
              at a time.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FEATURED MURAL */}
      <section className="px-5 md:px-10 mt-16">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <Link
              to={`/ourwork/muralarts/${featured.id}`}
              className="group block relative overflow-hidden bg-[var(--bg-elevated)] editorial-frame"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12">
                <div className="lg:col-span-7 aspect-[16/10] overflow-hidden">
                  <img
                    src={featured.img1}
                    alt={featured.title}
                    className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                  />
                </div>
                <div className="lg:col-span-5 p-8 md:p-12 flex flex-col justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--clay)]">
                      — Featured Mural · 01
                    </div>
                    <h2 className="mt-4 font-display font-light text-[clamp(2rem,4vw,3.5rem)] leading-[1.05] tracking-tight">
                      {featured.title}
                    </h2>
                    <p className="mt-6 text-[var(--muted)] text-[15px] leading-relaxed">
                      {featured.cardtext}
                    </p>
                  </div>
                  <div className="mt-10 flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-[0.32em] text-[var(--muted)]">
                      See full case study
                    </span>
                    <span className="w-12 h-12 grid place-items-center rounded-full border border-[var(--bone)] text-[var(--bone)] group-hover:bg-[var(--clay)] group-hover:border-[var(--clay)] transition-colors">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M7 17L17 7M9 7h8v8" />
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ALL MURAL ARTS */}
      <section
        id="muralart"
        className="px-5 md:px-10 mt-24 md:mt-40 scroll-mt-32"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-12 gap-6 mb-12">
            <div className="col-span-12 md:col-span-4">
              <Reveal>
                <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--clay)]">
                  — The Wall Index
                </div>
                <div className="mt-3 font-display text-5xl md:text-7xl text-[var(--bone)]">
                  {String(rest.length + 1).padStart(2, "0")}
                </div>
              </Reveal>
            </div>
            <div className="col-span-12 md:col-span-8">
              <Reveal delay={120}>
                <h2 className="font-display font-light text-[clamp(2.5rem,5vw,4.5rem)] leading-[1.05] tracking-tight">
                  Every wall, a{" "}
                  <span className="italic font-extralight">story</span>.
                </h2>
                <p className="mt-6 text-[var(--muted)] text-[15px] leading-relaxed max-w-xl">
                  Hand-painted stories across India — cafés, schools, homes,
                  flyovers, heritage sites, and the spaces in between.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-3 md:gap-4 auto-rows-min">
            {rest.map((p, i) => {
              const layouts = [
                "col-span-12 md:col-span-7 aspect-[16/10]",
                "col-span-6 md:col-span-5 aspect-[4/5]",
                "col-span-6 md:col-span-5 aspect-[4/5]",
                "col-span-6 md:col-span-4 aspect-[4/3]",
                "col-span-6 md:col-span-4 aspect-[4/3]",
                "col-span-12 md:col-span-4 aspect-[16/9]",
                "col-span-12 md:col-span-6 aspect-[16/9]",
                "col-span-12 md:col-span-6 aspect-[16/9]",
                "col-span-12 md:col-span-8 aspect-[16/9]",
                "col-span-12 md:col-span-4 aspect-[4/3]",
                "col-span-12 md:col-span-6 aspect-[16/9]",
                "col-span-12 md:col-span-6 aspect-[16/9]",
                "col-span-6 md:col-span-5 aspect-[4/5]",
                "col-span-6 md:col-span-7 aspect-[16/10]",
              ];
              return (
                <Reveal key={p.id} delay={(i % 6) * 60}>
                  <Link
                    to={`/ourwork/muralarts/${p.id}`}
                    className={`group relative block overflow-hidden bg-[var(--bg-elevated)] ${layouts[i % layouts.length]}`}
                  >
                    <img
                      src={p.img1}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-[var(--ink)]/40 group-hover:bg-[var(--ink)]/20 transition-colors duration-500" />

                    <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.32em] text-[var(--bone)]/80 flex items-center gap-2">
                      <span className="font-display text-3xl text-[var(--clay)] tabular-nums leading-none">
                        0{i + 2}
                      </span>
                      <span className="block w-6 h-px bg-[var(--bone)]/60" />
                    </div>

                    <div className="absolute left-0 right-0 bottom-0 p-4 md:p-6 flex items-end justify-between">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.28em] text-[var(--bone)]/70">
                          Mural
                        </div>
                        <h3 className="font-display text-lg md:text-2xl text-[var(--bone)] leading-tight">
                          {p.title}
                        </h3>
                      </div>
                      <span className="w-10 h-10 grid place-items-center rounded-full border border-[var(--bone)] text-[var(--bone)] -rotate-45 group-hover:rotate-0 group-hover:bg-[var(--clay)] group-hover:border-[var(--clay)] transition-all duration-500">
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M5 12h14M13 5l7 7-7 7" />
                        </svg>
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* OTHER PRACTICES — TABBED */}
      <section
        id="otherpractices"
        className="px-5 md:px-10 mt-24 md:mt-40 scroll-mt-32"
      >
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="border-t border-b border-[var(--line)] py-6 flex flex-col md:flex-row md:items-baseline gap-4 md:gap-8">
              <span className="text-[10px] uppercase tracking-[0.32em] text-[var(--clay)]">
                — Beyond walls
              </span>
              <h2 className="font-display text-2xl md:text-3xl text-[var(--bone)]">
                We also work in{" "}
                <span className="italic font-extralight">graphic design</span>,{" "}
                <span className="italic font-extralight">sculpture</span>,{" "}
                <span className="italic font-extralight">painting</span> &{" "}
                <span className="italic font-extralight">portraiture</span>.
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-12 gap-6 mt-12">
            <Reveal className="col-span-12 md:col-span-4">
              <Link
                to="/gallery?type=graphic"
                className="block group editorial-frame p-6 md:p-8 bg-[var(--bg-soft)]"
              >
                <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--clay)]">
                  Practice · 01
                </div>
                <h3 className="mt-4 font-display text-3xl text-[var(--bone)]">
                  Graphic Design
                </h3>
                <p className="mt-4 text-[var(--muted)] text-sm leading-relaxed">
                  Posters, identities, and editorial design — built for places
                  with something to say.
                </p>
                <div className="mt-6 text-[10px] uppercase tracking-[0.32em] text-[var(--bone)] group-hover:text-[var(--clay)] transition-colors">
                  See work →
                </div>
              </Link>
            </Reveal>
            <Reveal className="col-span-12 md:col-span-4" delay={120}>
              <Link
                to="/gallery?type=sculpture"
                className="block group editorial-frame p-6 md:p-8 bg-[var(--bg-soft)]"
              >
                <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--clay)]">
                  Practice · 02
                </div>
                <h3 className="mt-4 font-display text-3xl text-[var(--bone)]">
                  Sculpture
                </h3>
                <p className="mt-4 text-[var(--muted)] text-sm leading-relaxed">
                  Three-dimensional works — clay, plaster, found objects. Built
                  with the same hand that paints.
                </p>
                <div className="mt-6 text-[10px] uppercase tracking-[0.32em] text-[var(--bone)] group-hover:text-[var(--clay)] transition-colors">
                  See work →
                </div>
              </Link>
            </Reveal>
            <Reveal className="col-span-12 md:col-span-4" delay={240}>
              <Link
                to="/gallery?type=painting"
                className="block group editorial-frame p-6 md:p-8 bg-[var(--bg-soft)]"
              >
                <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--clay)]">
                  Practice · 03
                </div>
                <h3 className="mt-4 font-display text-3xl text-[var(--bone)]">
                  Painting & Portrait
                </h3>
                <p className="mt-4 text-[var(--muted)] text-sm leading-relaxed">
                  Canvas paintings and commissioned portrait work. Quiet pieces,
                  painted slowly, for collectors and clients.
                </p>
                <div className="mt-6 text-[10px] uppercase tracking-[0.32em] text-[var(--bone)] group-hover:text-[var(--clay)] transition-colors">
                  See work →
                </div>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-5 md:px-10 mt-24 md:mt-40">
        <div className="max-w-7xl mx-auto border-t border-[var(--line)] pt-12 flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--clay)]">
              — Have a wall?
            </div>
            <h3 className="mt-3 font-display text-3xl md:text-5xl text-[var(--bone)] leading-tight max-w-xl">
              See a wall you'd like us to paint next?
            </h3>
          </div>
          <Link
            to="/booknow"
            className="group inline-flex items-center gap-3 px-7 py-4 bg-[var(--clay)] text-[var(--bone)] rounded-full tracking-wide hover:bg-[var(--bone)] hover:text-[var(--ink)] transition-colors"
          >
            Commission a wall
            <span className="relative w-4 h-4 overflow-hidden">
              <span className="absolute inset-0 transition-transform duration-500 group-hover:translate-x-5 group-hover:-translate-y-5">
                →
              </span>
              <span className="absolute inset-0 -translate-x-5 translate-y-5 transition-transform duration-500 group-hover:translate-x-0 group-hover:translate-y-0">
                →
              </span>
            </span>
          </Link>
        </div>
      </section>
    </main>
  );
}